import { useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined'
import { Collapse, Menu, Typography } from '@mui/material'
import Badge from '@mui/material/Badge'
import { useBreakpoints } from 'src/feature/theme/hook/useBreakpoints'
import { NavListItem } from '../../../../../component/common/original/molecule/NavListItem'
import { SxHelperStyle } from '../../../../theme/style/styleUtilities'
import { AppDivider } from 'src/component/common/analog/molecule/AppDivider'
import { useMyTeamStore } from 'src/feature/my-team/store/my-team.store'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Word } from 'src/feature/localization/component/atom/Word'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { Palette } from 'src/feature/theme/model/Palette'
import { useNavigate } from 'react-router-dom'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { UserNotification, UserNotificationType } from 'src/feature/my-team/api/my-team-dashboard-api'
import { useProjectBuilderStore } from 'src/feature/project-builder/store/project-builder.store'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useSkillAssessmentStore } from 'src/feature/skill-assessment/store/skill-assessment.store'

export const NotificationLink = observer(() => {
  const [showMenu, setShowMenu] = useState(false)
  const [readNotifications, setReadNotifications] = useState<string[]>([])
  const [showReadNotifications, setShowReadNotifications] = useState(false)
  const menuItemRef = useRef<HTMLDivElement | null>(null)
  const { isMd } = useBreakpoints()
  const myTeamStore = useMyTeamStore()
  const projectBuilderStore = useProjectBuilderStore()
  const skillAssessmentStore = useSkillAssessmentStore()
  const { translation } = useLocalizationStore()
  const navigate = useNavigate()

  const pageTranslation = translation.pages.notificationsPanel

  useEffect(() => {
    myTeamStore.getUserNotifications()
    // Load skill categories so skill-assessment notifications can determine which individual skills are still unrated.
    skillAssessmentStore.getSkillSets()
  }, [myTeamStore])

  useEffect(() => {
    const readIds = myTeamStore.userNotifications
      ?.filter((notification) => {
        const storedValue = localStorage.getItem(`read-${notification.id}`)

        if (!storedValue) {
          return false
        }

        // Only project-response and skill-validation notifications are treated as read notifications
        return notification.type === 'project-response' || notification.type === 'skill-validation'
      })
      .map(notification => notification.id) ?? []

    setReadNotifications(readIds)
  }, [myTeamStore.userNotifications])

  const handleOpen = () => {
    setShowMenu(!showMenu)
  }

  const handleClose = () => {
    setShowMenu(false)
  }

  const handleProjectInvitationResponse = async (
    projectId: string,
    accepted: boolean,
  ) => {
    await projectBuilderStore.respondToProjectInvitation(
      projectId,
      accepted,
    )
    await myTeamStore.getUserNotifications()
  }

  const getNotificationTitle = (type: UserNotificationType) => {
    switch (type) {
    case 'skill-assessment':
      return pageTranslation.skillAssessment()
    case 'skill-validation':
      return pageTranslation.skillValidation()
    case 'performance-review':
      return pageTranslation.performanceReview()
    case 'project-invitation':
      return pageTranslation.projectBuilder()
    case 'project-response':
      return pageTranslation.projectBuilder()
    default:
      return 'NA'
    }
  }

  // The SkillCategory is loaded from skillAssessmentStore so we can determine which individual skills still need to be assessed
  const getSkillAssessmentMessage = (
    notification: Extract<UserNotification, { type: 'skill-assessment' }>,
  ) => {
    const category = skillAssessmentStore.skillSets?.find(
      category => category.id === notification.skillSetId,
    )

    // Skills without a selected level are considered unrated
    const unratedSkills = category?.skills.filter(
      skill => !skill.selectedSetLevel,
    ) ?? []

    // More than three unrated skills uses the general message, otherwise comma-separated list
    if (unratedSkills.length > 3) {
      return pageTranslation.skillAssessmentManyMsg(
        notification.skillSetName,
      )
    }

    // Comma-separated skill list as the old implementation when there are three or fewer unrated skills.
    return pageTranslation.skillAssessmentMultipleMsg(
      unratedSkills.map(skill => skill.setName).join(', '),
      notification.skillSetName,
    )
  }

  const getNotificationMessage = (notification: UserNotification) => {
    switch (notification.type) {
    case 'skill-assessment':
      return getSkillAssessmentMessage(notification)
    case 'skill-validation':
      return pageTranslation.skillValidationMsg(notification.userName)
    case 'performance-review':
      return notification.reason === 'assigned'
        ? pageTranslation.performanceReviewAssignedMsg(notification.templateName)
        : pageTranslation.performanceReviewMsg(notification.templateName)
    case 'project-invitation':
      return pageTranslation.projectInvitationMsg(notification.projectName)
    case 'project-response':
      return notification.response === 'accepted'
        ? pageTranslation.projectResponseAccepted(
          notification.userName,
          notification.projectName,
        )
        : pageTranslation.projectResponseDeclined(
          notification.userName,
          notification.projectName,
        )
    default:
      return ''
    }
  }

  const handleMarkAsRead = (notification: { id: string }) => {
    localStorage.setItem(
      `read-${notification.id}`,
      JSON.stringify({
        readAt: new Date().toISOString(),
      }),
    )
    setReadNotifications((prev) => {
      if (prev.includes(notification.id)) {
        return prev
      }
      
      return [...prev, notification.id]
    })
  }

  // Project-response and handled skill-validation notifications that have not been marked as read remain visible in the main list
  const visibleNotifications = myTeamStore.userNotifications?.filter(
    notification => notification.type !== 'project-response' && notification.type !== 'skill-validation' || !readNotifications.includes(notification.id),
  ) ?? []

  // Read project-response and skill-validation notifications are kept separately so they can still be viewed
  const readNotificationItems = myTeamStore.userNotifications?.filter(
    notification => (notification.type === 'project-response' || notification.type === 'skill-validation') && readNotifications.includes(notification.id),
  ) ?? []

  const renderNotifications = () => {
    const groupedNotifications = visibleNotifications.reduce((acc, notification) => {
      if (!acc[notification.type]) {
        acc[notification.type] = []
      }
      acc[notification.type].push(notification)

      return acc
    }, {} as Record<UserNotificationType, UserNotification[]>)

    return (
      <>
        {visibleNotifications.length === 0 ? (
          <AppBox sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '30vh', minWidth: 345 }}>
            <Caption sx={{ color: 'text.secondary', fontSize: 11, textAlign: 'center' }}>
              {pageTranslation.noNotifications()}
            </Caption>
          </AppBox>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: '30vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {Object.entries(groupedNotifications).map(([type, notifications]) => (
              <Box key={type}>
                <Typography sx={{ fontSize: 11, color: 'secondary.main', fontWeight: 500, mb: 1 }}>
                  {getNotificationTitle(type as UserNotificationType)}
                </Typography>
                {notifications.map(notification => (
                  <AppBox key={notification.id} sx={styles.notificationItem}>
                    <Typography fontSize={11}>
                      <Word fontSize={11} sx={{ color: 'text.secondary', marginRight: 5 }}>
                        {notification.date}:
                      </Word>
                      {getNotificationMessage(notification)}
                      {notification.type === 'skill-assessment' && (
                        <Word
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: Palette.HAN_PURPLE,
                            marginLeft: 5,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => navigate(appRoutePath.userDashboard)}
                        >
                          {pageTranslation.assessSkills()}
                        </Word>
                      )}

                      { notification.type === 'skill-validation' && (
                        <Word
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: Palette.HAN_PURPLE,
                            marginLeft: 5,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => {
                            handleMarkAsRead(notification)
                            navigate(appRoutePath.MyTeamUserDetails(notification.userId), {
                              state: {
                                initialUserMeta: {
                                  userId: notification.userId,
                                  name: notification.userName,
                                  email: '',
                                  avatarUrl: '',
                                  memberSince: '',
                                },
                              },
                            })
                          }}
                        >
                          {pageTranslation.validate()}
                        </Word>
                      )}
                      {notification.type === 'performance-review' && (
                        <Word
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: Palette.HAN_PURPLE,
                            marginLeft: 5,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => navigate(appRoutePath.profileInfo)}
                        >
                          {pageTranslation.answerReview()}
                        </Word>
                      )}
                      {notification.type === 'project-invitation' && (
                        <>
                          <Word
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: Palette.HAN_PURPLE,
                              marginLeft: 5,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() => handleProjectInvitationResponse(notification.projectId, true)}
                          >
                            {pageTranslation.accept()}
                          </Word>

                          <Word
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: Palette.HAN_PURPLE,
                              marginLeft: 15,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                            onClick={() => handleProjectInvitationResponse(notification.projectId, false)}
                          >
                            {pageTranslation.decline()}
                          </Word>
                        </>
                      )}
                      {notification.type === 'project-response' && (
                        <Word
                          sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: Palette.HAN_PURPLE,
                            marginLeft: 5,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => handleMarkAsRead(notification)}
                        >
                          {pageTranslation.markAsRead()}
                        </Word>
                      )}
                    </Typography>
                  </AppBox>
                ))}
              </Box>
            ))}
          </Box>
        )}
        {/* Expandable section for read notifications */}
        {readNotificationItems.length > 0 && (
          <Box sx={{ marginTop: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1,
                gap: 5,
              }}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation()
                setShowReadNotifications(prev => !prev)
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: 'secondary.main',
                  fontWeight: 500,
                }}
              >
                {pageTranslation.readNotifications()}
              </Typography>

              {showReadNotifications ? (
                <ExpandLessIcon
                  sx={{ fontSize: 18, color: 'secondary.main' }}
                />
              ) : (
                <ExpandMoreIcon
                  sx={{ fontSize: 18, color: 'secondary.main' }}
                />
              )}
            </Box>

            {/* Show/hide read notifications when the section is expanded */}
            <Collapse in={showReadNotifications}>
              {readNotificationItems.map(notification => (
                <AppBox key={notification.id} sx={styles.notificationItem}>
                  <Typography fontSize={11}>
                    <Word
                      fontSize={11}
                      sx={{ color: 'text.secondary', marginRight: 5 }}
                    >
                      {notification.date}:
                    </Word>

                    {getNotificationMessage(notification)}
                  </Typography>
                </AppBox>
              ))}
            </Collapse>
          </Box>
        )}
      </>
    )
  }
  
  return (
    <Box display="flex" flexDirection="column" alignItems="center" ref={menuItemRef}>
      <NavListItem
        onClick={handleOpen}
        isMain={true}
        sx={styles.root}
      >
        <Badge
          badgeContent={visibleNotifications.length}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: Palette.DANGER,
              color: Palette.WHITE,
            },
          }}
        >
          <NotificationsOutlinedIcon fontSize="medium" />
        </Badge>
      </NavListItem>
      {isMd ? (
        <Collapse in={showMenu}>
          <Box sx={styles.notificationList}>
            <Typography>{pageTranslation.title()}</Typography>
            <AppDivider sx={{ margin: '5px 0' }} />
            {renderNotifications()}
          </Box>
        </Collapse>
      ) : (
        <Menu
          anchorEl={menuItemRef.current}
          id="notification-menu"
          open={showMenu}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          PaperProps={{
            elevation: 0,
            sx: {
              width: 370,
              minHeight: '30vh',
              maxHeight: '60vh',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 17,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 5,
                width: 10,
                height: 10,
                backgroundColor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          aria-hidden={!showMenu}
        >
          <Box>
            <Typography>{pageTranslation.title()}</Typography>
            <AppDivider sx={{ margin: '5px 0' }} />
            {renderNotifications()}
          </Box>
        </Menu>
      )}
    </Box>
  )
})

const styles = {
  root: {
    ...SxHelperStyle.flexCenter,
    flexDirection: 'row',
    color: 'text.primary',
  },
  notificationList: {
    width: 370,
    minHeight: '30vh',
    maxHeight: '60vh',
    overflowY: 'auto',
    backgroundColor: 'background.paper',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.32)',
    borderRadius: '10px',
    padding: '10px',
  },
  notificationItem: {
    padding: '10px 0',
    borderBottom: '1px solid #e0e0e040',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}